import {
  FC,
  JSX,
  useEffect,
  useMemo
} from "react";
import {
  Text,
  View,
} from "react-native";
import { useLocalSearchParams } from "expo-router";
import { useGetPersonReportQuery } from "@/_store/person/person.slice";
import { useUpdateReportMutation } from "@/_store/report/report.slice";
import useReportHandler from "@/_hooks/Report/useReportHandler";
import { ReportItem, useReportByType } from "@/_hooks/Report/useReportByType";
import { ReportTypePerson } from "@/types/reportType";
import List, { ListItems } from "@/app/components/List";
import RiskIndicator from "@/app/components/RiskIndicator/RiskIndicator";
import CustomButton from "@/app/components/CustomButton";
import Divider from "@/app/components/Divider";
import InfoIcon from "@/assets/icons/main/blueAlert.svg";
import AlertIcon from "@/assets/icons/main/redAlert.svg";
import RefreshIcon from "@/assets/icons/main/refresh.svg";
import DownloadIcon from "@/assets/icons/main/download.svg";
import { toast } from "sonner-native";
import dayjs from "dayjs";
import { POLLING_INTERVAL_MS, STATUS_COMPLETE, STATUS_IN_PROGRESS } from "@/_utils/contstans";
import Container from "@/app/components/Container";
import { EdgeInsets, useSafeAreaInsets } from "react-native-safe-area-context";

interface ISuccessCheckProps {
}

const SuccessUserCheck: FC<ISuccessCheckProps> = (): JSX.Element => {
  const { id: personReportId } = useLocalSearchParams();
  const insets: EdgeInsets = useSafeAreaInsets();
  const {
    isPollingWhileGeneratingFile,
    IsShouldPollReportData,
    setIsShouldPollReportData,
    handleGenerate,
  } = useReportHandler({ id: personReportId! as string, isFiz: true });

  const [updateReport, { isLoading }] = useUpdateReportMutation();
  const { currentData, refetch, isLoading: isPersonReportLoading } = useGetPersonReportQuery(
    { id: personReportId! as string },
    {
      skip: !personReportId,
      pollingInterval: IsShouldPollReportData ? POLLING_INTERVAL_MS : 0,
    }
  );

  const {
    created,
    responseData
  } = currentData ?? {}

  const reportCreatedAt: string = `Сформирован ${created ? dayjs(created).format("DD MMM YYYY · HH:mm") : "-"}`;

  const reportByType = useReportByType(responseData);

  const personalInfo: ReportItem | undefined = reportByType[ReportTypePerson.MainInfo];
  const passportInfo: ReportItem | undefined = reportByType[ReportTypePerson.ExpiredPassport];
  const massLeaders: ReportItem | undefined = reportByType[ReportTypePerson.MassLeaders];
  const bankruptcyInfo: ReportItem | undefined = reportByType[ReportTypePerson.FedresursShort];
  const pledgesInfo: ReportItem | undefined = reportByType[ReportTypePerson.Pledge];
  const egripEgrulInfo: ReportItem | undefined = reportByType[ReportTypePerson.EgripEgrul];
  const statusSelfEmployment: ReportItem | undefined = reportByType[ReportTypePerson.StatusNpdNalog];
  const executiveProceeding: ReportItem | undefined = reportByType[ReportTypePerson.ExecutiveProceeding];
  const extremistInfo: ReportItem | undefined = reportByType[ReportTypePerson.Extremist];
  const wantedCriminalInfo: ReportItem | undefined = reportByType[ReportTypePerson.WantedCriminal];
  const driverInfo: ReportItem | undefined = reportByType[ReportTypePerson.DlMain];

  const blocks = useMemo<(ReportItem | undefined)[]>(() => [
    personalInfo,
    passportInfo,
    massLeaders,
    bankruptcyInfo,
    pledgesInfo,
    egripEgrulInfo,
    statusSelfEmployment,
    executiveProceeding,
    extremistInfo,
    wantedCriminalInfo,
    driverInfo,
  ], [
    personalInfo,
    passportInfo,
    massLeaders,
    bankruptcyInfo,
    pledgesInfo,
    egripEgrulInfo,
    statusSelfEmployment,
    executiveProceeding,
    extremistInfo,
    wantedCriminalInfo,
    driverInfo,
  ]);

  const isAnyInProgress: boolean = blocks.some((block) => block?.status === STATUS_IN_PROGRESS);
  const isAllComplete: boolean = blocks.every((block) => !block || block.status === STATUS_COMPLETE);

  const passportValue: string = passportInfo?.data?.series && passportInfo?.data?.number ?
    `${passportInfo.data.series} ${passportInfo.data.number}` : "Информация отсутствует"

  const PERSONAL_INFO: ListItems[] = [
    { label: "ФИО", value: personalInfo?.data?.full_name },
    { label: "Номер, серия паспорта", value: passportValue },
    { label: "ИНН", value: personalInfo?.data?.inn },
  ];

  const FINANCIAL_INFO: ListItems[] = [
    { label: "Банкротство", value: bankruptcyInfo?.short_report },
    { label: "Залоги движимого имущества", value: pledgesInfo?.short_report },
    { label: "Проверки по ЕГРИТ/ЕГРЮЛ", value: egripEgrulInfo?.short_report },
    { label: "Проверка статуса самозанятого", value: statusSelfEmployment?.data?.message },
    { label: "Массовые руководители", value: massLeaders?.short_report },
  ];

  const LEGAL_INFO: ListItems[] = [
    { label: "Исполнительные производства", value: executiveProceeding?.short_report },
    { label: "Реестр террористов и экстремистов", value: extremistInfo?.short_report },
  ];

  const STATUS_INFO: ListItems[] = [
    { label: "Нахождение в розыске", value: wantedCriminalInfo?.short_report},
    { label: "Проверка ВУ",
      value: driverInfo?.data?.driverLicense?.isValidDocument === undefined ?
        "Информация отсутствует" : driverInfo?.data?.driverLicense?.isValidDocument ?
          "Документ действителен" : "Документ недействителен или срок истёк"
    },
    { label: "Проверка штрафов по ВУ", value: driverInfo?.data?.driverLicense?.decisions?.length ?
        `Количество штрафов: ${driverInfo.data.driverLicense.decisions.length}`
        : "Информация отсутствует" },
  ];

  const isLoadingReportData: boolean = isPersonReportLoading
    || !personalInfo?.data?.full_name
    || isAnyInProgress;

  const isInfoLoading: boolean = isPersonReportLoading || isAnyInProgress || isLoading;

  const isShowDownloadButton: boolean =
    !IsShouldPollReportData &&
    Boolean(personalInfo?.data?.full_name) &&
    !isPollingWhileGeneratingFile;

  useEffect(() => {
    if (!isLoadingReportData) {
      toast.success("Отчет сформирован", {
        description: `Физ.лицо: ${personalInfo?.data.full_name}`
      });
    };

  }, [isLoadingReportData, personalInfo]);

  useEffect(() => {
    if (isAnyInProgress) {
      setIsShouldPollReportData(true);

      toast.info("Отчет формируется", {
        description: "Нам нужно некоторое время для формирования отчета. Пожалуйста, подождите.",
        icon: <InfoIcon />,
      });
    } else if (isAllComplete) {
      setIsShouldPollReportData(false);
    }
  }, [isAnyInProgress, isAllComplete, isLoading]);

  const handleRefresh = async (): Promise<void> => {
    try {
      await updateReport({ id: personReportId as string, is_fiz: true }).unwrap();
      await refetch();
    } catch (error: any) {
      console.error("Ошибка при обновлении отчета:", error);
      const errorMessage = error?.data?.error || "При обновлении отчета произошла ошибка";

      toast.error(errorMessage, {icon: <AlertIcon />});
    }
  };

  return (
    <Container style={{ height: 100, flexGrow: 1, paddingHorizontal: 16, paddingBottom: insets.bottom + 100 }}>
      <View className="flex-1">
        {isInfoLoading ? (
          <Text className="mb-2 text-center text-blue-500">
            Формируем отчет ...
          </Text>
        ) : (
          <Text className="mb-2 text-subhead text-center">
            {reportCreatedAt}
          </Text>
        )}
        <Text className="text-header text-center mb-6">
          {personalInfo?.data?.full_name || "Информация отсутствует"}
        </Text>
        {isInfoLoading ? (
          <RiskIndicator level="wait" />
        ) : (
          <View className="flex flex-row justify-center">
            <View className="flex flex-col items-center mr-6">
              <CustomButton
                title=""
                className="w-12 h-12 bg-gray-light rounded-full"
                suffixIcon={<RefreshIcon stroke="black" />}
                suffixIconClass="!mr-0"
                onPress={handleRefresh}
              />
              <Text className="text-center stats-text">Обновить<br/>отчет</Text>
            </View>
            {isShowDownloadButton && (
                <View className="flex flex-col items-center">
                  <CustomButton
                    title=""
                    className="w-12 h-12 bg-gray-light rounded-full"
                    suffixIcon={<DownloadIcon stroke="black" />}
                    suffixIconClass="!mr-0"
                    onPress={handleGenerate}
                    disabled={!isShowDownloadButton}
                  />
                  <Text className="text-center stats-text">Скачать<br/>полный отчет</Text>
                </View>
              )
            }
          </View>
        )}
        <Divider />
        <List items={PERSONAL_INFO} isLoading={isInfoLoading} />
        <Divider />
        <List items={FINANCIAL_INFO} isLoading={isInfoLoading} />
        <Divider />
        <List items={LEGAL_INFO} isLoading={isInfoLoading} />
        <Divider />
        <List items={STATUS_INFO} isLoading={isInfoLoading} />
      </View>
    </Container>
  );
};

export default SuccessUserCheck;
